import { CssBaseline } from '@mui/material';
import { createTheme, StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import { ShapeOptions } from '@mui/system';
import React from 'react';
import { useThemeController } from 'src/hooks/useThemeController';
import components from './components';
import a11yPalette from './palettes/a11y';
import lightPalette from './palettes/light';
import typography from './typography';

declare module '@mui/material/styles' {
  interface Palette {
    // a11y: Palette['primary'];
    gradient: Palette['primary'];
    light: Palette['grey'];
  }
  interface PaletteOptions {
    // a11y: PaletteOptions['primary'];
    gradient: PaletteOptions['primary'];
    light: PaletteOptions['grey'];
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    subtitle3: React.CSSProperties;
    body3: React.CSSProperties;
    button_lg: React.CSSProperties;
    button_sm: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    subtitle3?: React.CSSProperties;
    body3?: React.CSSProperties;
    button_lg?: React.CSSProperties;
    button_sm?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle3: true;
    body3: true;
    button_lg: true;
    button_sm: true;
  }
}

export const shape: ShapeOptions = {
  borderRadius: 8,
};

export const breakpoints = {
  values: {
    xs: 0,
    sm: 816,
    md: 1072,
    lg: 1188,
    xl: 1400,
  },
};

const lightTheme = createTheme({
  palette: lightPalette,
  typography,
  components,
  shape,
  breakpoints,
});

const a11yTheme = createTheme({
  palette: a11yPalette,
  typography,
  components,
  shape,
  breakpoints,
});

// export default lightTheme as Theme;

const themeMap: { [key: string]: Theme } = {
  light: lightTheme,
  a11y: a11yTheme,
};

function getThemeByName(theme: string): Theme {
  return themeMap[theme];
}

export type ThemeContextProps = {
  currentTheme: string;
  setTheme: (arg: string) => void | undefined;
};

export const ThemeContext = React.createContext<Partial<ThemeContextProps>>({});

export const AppThemeProvider = (props: Record<string, any>) => {
  const { theme, contextValue } = useThemeController(getThemeByName);

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {props.children}
        </ThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
};
